import { KBArticle, Platform } from "@/types";

const STOP_WORDS = new Set([
  "a", "an", "and", "are", "as", "at", "be", "but", "by", "can", "do", "for",
  "from", "have", "i", "in", "is", "it", "of", "on", "or", "that", "the",
  "this", "to", "was", "will", "with", "you", "your", "my", "me", "we",
]);

function tokenize(text: string): string[] {
  return text.toLowerCase()
    .replace(/[^a-z0-9\s]+/g, " ")
    .split(/\s+/)
    .filter((w) => w.length >= 3 && !STOP_WORDS.has(w));
}

/**
 * Score articles against a query string. Higher = more relevant.
 * - Title hits: 10 each
 * - Body hits: 1 each
 * - Multi-word phrase in title: +20 bonus
 * - Platform match: ×1.6 boost
 * - Wrong-platform article (has platforms set, none match user's): ×0.4 penalty
 * - AI-suggested title: gets a strong head start
 */
export function searchKBArticles(
  articles: KBArticle[],
  query: string,
  platform: Platform | null,
  aiSuggestedTitle?: string,
): KBArticle[] {
  const words = tokenize(query);
  if (words.length === 0 && !aiSuggestedTitle) return [];

  const aiKey = aiSuggestedTitle?.toLowerCase().trim();

  const scored = articles.map((a) => {
    const titleLc = a.title.toLowerCase();
    const bodyLc = (a.body || "").toLowerCase();
    let score = 0;

    for (const w of words) {
      if (titleLc.includes(w)) score += 10;
      if (bodyLc.includes(w)) score += 1;
    }

    // Bonus for full multi-word phrase in title
    if (words.length >= 2 && titleLc.includes(words.join(" "))) score += 20;

    // AI-suggested title — fuzzy match
    if (aiKey && (titleLc === aiKey || titleLc.includes(aiKey) || aiKey.includes(titleLc))) {
      score += 50;
    }

    // Platform weighting
    const ps = a.platforms ?? [];
    if (platform) {
      if (ps.includes(platform)) score *= 1.6;
      else if (ps.length > 0) score *= 0.4;
    }

    return { article: a, score };
  });

  return scored
    .filter((x) => x.score >= 10)
    .sort((a, b) => b.score - a.score)
    .map((x) => x.article);
}
